import { useState, useEffect } from "react";
import useResponsive from "@/hooks/useResponsive";
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  message,
  Upload,
  Row,
  Col,
  Statistic,
  Divider,
  Rate,
  Typography,
  Spin,
} from "antd";

import {
  PlusOutlined,
  DeleteOutlined,
EditOutlined,
PictureOutlined,  
UploadOutlined,
  EyeOutlined,
  HomeOutlined,
  StarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import hotelService from "../services/hotelService";
import CreateHotelModal from "../components/modals/CreateHotelModal";

const { Title, Text } = Typography;

const HotelManagement_Enhanced = () => {
 const { isMobile } = useResponsive();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
const [editingHotel, setEditingHotel] = useState(null);  
const [searchText, setSearchText] = useState("");

  /* =====================================================
     FETCH HOTELS
  ===================================================== */

  useEffect(() => {
    fetchHotels();
  }, []);

  const fetchHotels = async () => {
    try {
      setLoading(true);

      const response =
  await hotelService.getAll({
    page: 1,
    limit: 500,
  });

      console.log("HOTELS API RESPONSE:", response?.data);

      const hotelsData =
        response?.data?.data ||
        response?.data?.hotels ||
        [];

      if (!Array.isArray(hotelsData)) {
        console.error(
          "Hotels response is not array:",
          hotelsData
        );

        setHotels([]);

        return;
      }

      setHotels(hotelsData);

    } catch (err) {
      console.error("FETCH HOTELS ERROR:", err);

      message.error(
        err?.response?.data?.message ||
        "Failed to load hotels"
      );

      setHotels([]);
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     VIEW HOTEL
  ===================================================== */

  const handleView = (hotel) => {
    Modal.info({
      title: hotel.name,
      width: 750,

      content: (
        <div style={{ marginTop: 20 }}>

          <p>
            <strong>City:</strong>{" "}
            {hotel.location?.city || "-"}
          </p>

          <p>
            <strong>State:</strong>{" "}
            {hotel.location?.state || "-"}
          </p>

          <p>
            <strong>Country:</strong>{" "}
            {hotel.location?.country || "-"}
          </p>

          <p>
            <strong>Star Rating:</strong>{" "}
            {hotel.starRating || 0}
          </p>

          <p>
            <strong>Category:</strong>{" "}
            {hotel.category || "-"}
          </p>

          <p>
            <strong>Phone:</strong>{" "}
            {hotel.contact?.phone || "-"}
          </p>

          <p>
            <strong>Email:</strong>{" "}
            {hotel.contact?.email || "-"}
          </p>

          <p>
            <strong>Amenities:</strong>{" "}
            {hotel.amenities?.join(", ") || "-"}
          </p>

          <Divider />

          <Title level={5}>
            Seasonal Pricing
          </Title>

          {hotel.seasonalRates?.length > 0 ? (

            hotel.seasonalRates.map(
              (season, index) => (
                <Card
                  key={index}
                  size="small"
                  style={{
                    marginBottom: 12,
                  }}
                >

                  <div
                    style={{
                      marginBottom: 12,
                    }}
                  >
                    <Text strong>
                      {season.seasonName}
                    </Text>

                    <br />

                    <Text type="secondary">
                      {new Date(
                        season.startDate
                      ).toLocaleDateString()}
                      {" - "}
                      {new Date(
                        season.endDate
                      ).toLocaleDateString()}
                    </Text>
                  </div>

                  {season.roomTypes?.map(
                    (room, roomIndex) => (
                      <div
                        key={roomIndex}
                        style={{
                          marginBottom: 14,
                          padding: 10,
                          border:
                            "1px solid #f0f0f0",
                          borderRadius: 6,
                        }}
                      >

                        <Text strong>
                          {room.roomCategory}
                        </Text>

                        <div
                          style={{
                            marginTop: 8,
                          }}
                        >
                          {room.mealPlans?.map(
                            (
                              meal,
                              mealIndex
                            ) => (
                              <Tag
                                key={mealIndex}
                                color="blue"
                                style={{
                                  marginBottom: 6,
                                }}
                              >
                                {
                                  meal.mealPlan
                                }
                                {" "}
                                ₹
                                {
                                  meal.rates
                                    ?.baseRate
                                }
                              </Tag>
                            )
                          )}
                        </div>

                      </div>
                    )
                  )}

                </Card>
              )
            )

          ) : (
            <Text type="secondary">
              No seasonal rates
            </Text>
          )}
        </div>
      ),
    });
  };

const handleEdit = (hotel) => {

  setEditingHotel(hotel);

  setModalVisible(true);
};

  /* =====================================================
     DELETE HOTEL
  ===================================================== */

  const handleDelete = async (id) => {
    Modal.confirm({
      title: "Delete Hotel",
      content:
        "Are you sure you want to delete this hotel?",

      okText: "Delete",
      okType: "danger",

      onOk: async () => {
        try {

          setLoading(true);

          await hotelService.delete(id);

          message.success(
            "Hotel deleted successfully"
          );

          fetchHotels();

        } catch (err) {

          console.error(err);

          message.error(
            err?.response?.data?.message ||
            "Delete failed"
          );

        } finally {
          setLoading(false);
        }
      },
    });
  };

  /* =====================================================
     IMPORT CSV
  ===================================================== */

  const uploadProps = {
    accept: ".csv,.xlsx,.xls",

    showUploadList: false,

    beforeUpload: async (file) => {
      try {

        const formData = new FormData();

        formData.append("file", file);

        setLoading(true);

        await hotelService.importHotels(
          formData
        );

        message.success(
          "Hotels imported successfully"
        );

        await fetchHotels();

      } catch (err) {

        console.error(
          "IMPORT ERROR:",
          err
        );

        message.error(
          err?.response?.data?.message ||
          "Import failed"
        );

      } finally {
        setLoading(false);
      }

      return false;
    },
  };

  /* =====================================================
     FILTER HOTELS
  ===================================================== */

  const filteredHotels = hotels.filter(
    (hotel) => {

      const search =
        (searchText || "")
          .toLowerCase()
          .trim();

      if (!search) return true;

      return (
        hotel.name
          ?.toLowerCase()
          .includes(search) ||

        hotel.location?.city
          ?.toLowerCase()
          .includes(search) ||

        hotel.location?.country
          ?.toLowerCase()
          .includes(search)
      );
    }
  );

  /* =====================================================
     TABLE COLUMNS
  ===================================================== */

  const columns = [
{
  title: "Hotel",

  key: "hotel",

  render: (_, record) => {

    const primaryImage =
      record.images?.find(
        (img) => img.isPrimary
      ) || record.images?.[0];

    return (
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
        }}
      >

        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 12,
            overflow: "hidden",
            background: "#f5f5f5",
            border: "1px solid #e5e7eb",
            flexShrink: 0,
          }}
        >

          {primaryImage?.url ? (

<img
  src={`${window.location.origin}${primaryImage.url}`}
  alt={record.name}
  style={{
    width: "100%",
    height: "100%",
    objectFit: "cover",
  }}
  onError={(e) => {
    console.error("Hotel image failed:", e.target.src);
  }}
/>

          ) : (

            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#9ca3af",
              }}
            >
              <PictureOutlined
                style={{
                  fontSize: 24,
                }}
              />
            </div>
          )}

        </div>

        <div>

          <div
            style={{
              fontWeight: 600,
              fontSize: 15,
              marginBottom: 4,
            }}
          >
            {record.name}
          </div>

          <Text type="secondary">
            {record.category ||
              "mid-range"}
          </Text>

          <br />

          <Text
            type="secondary"
            style={{
              fontSize: 12,
            }}
          >
            {record.images?.length || 0}
            {" "}images
          </Text>

        </div>

      </div>
    );
  },
},

    {
      title: "Location",

      key: "location",

      render: (_, record) => (
        <Space>

          <EnvironmentOutlined />

          <span>
            {record.location?.city ||
              "-"}
            ,{" "}
            {record.location?.country ||
              "-"}
          </span>

        </Space>
      ),
    },

    {
      title: "Rating",

      dataIndex: "starRating",

      key: "starRating",

      render: (rating) => (
        <Rate
          disabled
          value={rating || 0}
        />
      ),
    },

    {
      title: "Seasons",

      key: "seasonalRates",

      render: (_, record) =>
        record.seasonalRates
          ?.length || 0,
    },

    {
      title: "Amenities",

      key: "amenities",

      render: (_, record) => (
        <Space wrap>

          {(record.amenities || [])
            .slice(0, 3)
            .map((item) => (
              <Tag key={item}>
                {item}
              </Tag>
            ))}

        </Space>
      ),
    },

    {
      title: "Status",

      key: "status",

      render: (_, record) => (
        <Tag
          color={
            record.availability
              ?.isActive === false
              ? "red"
              : "green"
          }
        >
          {record.availability
            ?.isActive === false
            ? "INACTIVE"
            : "ACTIVE"}
        </Tag>
      ),
    },

    {
      title: "Actions",

      key: "actions",

      render: (_, record) => (
        <Space>

          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() =>
              handleView(record)
            }
          >
            View
          </Button>

<Button
  icon={<EditOutlined />}
  size="small"
  onClick={() =>
    handleEdit(record)
  }
>
  Edit
</Button>

          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() =>
              handleDelete(record._id)
            }
          >
            Delete
          </Button>

        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>

      {/* =====================================================
         STATS
      ===================================================== */}

      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: 20,
        }}
      >

        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title="Total Hotels"
              value={hotels.length}
              prefix={<HomeOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title="Active Hotels"
              value={
                hotels.filter(
                  (h) =>
                    h.availability
                      ?.isActive !==
                    false
                ).length
              }
              prefix={<StarOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title="Seasonal Contracts"
              value={hotels.reduce(
                (sum, h) =>
                  sum +
                  (h.seasonalRates
                    ?.length || 0),
                0
              )}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} md={6}>
          <Card>
            <Statistic
              title="Avg Rating"
              value={
                hotels.length > 0
                  ? (
                      hotels.reduce(
                        (
                          sum,
                          h
                        ) =>
                          sum +
                          (h.starRating ||
                            0),
                        0
                      ) /
                      hotels.length
                    ).toFixed(1)
                  : 0
              }
              prefix={<StarOutlined />}
            />
          </Card>
        </Col>

      </Row>

      {/* =====================================================
         TABLE
      ===================================================== */}

      <Card>

<Row
  justify="space-between"
  align={isMobile ? "stretch" : "middle"}
  gutter={[16, 16]}
  style={{
    marginBottom: 20,
  }}
>

  <Col xs={24} lg={10}>

    <Title
      level={4}
      style={{ margin: 0 }}
    >
      Hotel Inventory
    </Title>

    <Text type="secondary">
      Manage hotel inventory,
      seasonal pricing and contracts
    </Text>

  </Col>

  <Col xs={24} lg={14}>

    <Space
      direction={isMobile ? "vertical" : "horizontal"}
      style={{
        width: "100%",
        justifyContent: isMobile ? "stretch" : "flex-end",
      }}
    >

      <Input.Search
        placeholder="Search by hotel or city..."
        allowClear
        style={{
          width: isMobile ? "100%" : 320,
        }}
        value={searchText}
        onChange={(e) =>
          setSearchText(e.target.value)
        }
      />

      <Upload {...uploadProps}>
        <Button
          block={isMobile}
          icon={<UploadOutlined />}
        >
          Import CSV
        </Button>
      </Upload>

      <Button
        block={isMobile}
        type="primary"
        icon={<PlusOutlined />}
        onClick={() =>
          setModalVisible(true)
        }
      >
        Add Hotel
      </Button>

    </Space>

  </Col>

</Row>

        <Spin spinning={loading}>

<Table
  columns={columns}
  dataSource={filteredHotels}
  rowKey="_id"
  scroll={{ x: 1200 }}
  pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
            }}
          />

        </Spin>

      </Card>

      {/* =====================================================
         CREATE HOTEL MODAL
      ===================================================== */}
<CreateHotelModal
  isOpen={modalVisible}
  hotel={editingHotel}
  onClose={() => {

    setModalVisible(false);

    setEditingHotel(null);
  }}
  onSuccess={() => {

    setModalVisible(false);

    setEditingHotel(null);

    fetchHotels();
  }}
/>

    </div>
  );
};

export default HotelManagement_Enhanced;
